import type { PublicDocumentResponse } from '@contracts/order/PublicOrderResponse';
import ListItemButton from '@mui/material/ListItemButton';
import { List, ListItemIcon, ListItemText } from '@src/common/components/List';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { getFileIcon } from '@src/common/utils/FileIcon';
import { useTranslation } from 'react-i18next';

interface Props {
  documents: PublicDocumentResponse[];
  onDownload: (document: PublicDocumentResponse) => void;
}

export const PublicDocumentList = ({ documents, onDownload }: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  if (documents.length === 0) return null;

  const downloadDocument = async (document: PublicDocumentResponse) => {
    try {
      await onDownload(document);
    } catch {
      showToast({
        severity: ToastLevel.error,
        message: t('publicPortal.downloadError'),
      });
    }
  };
  const getDocumentName = (doc: PublicDocumentResponse) =>
    doc.displayName ?? doc.key.substring(doc.key.lastIndexOf('/') + 1);

  return (
    <Stack>
      <List disablePadding>
        {documents.map((doc) => {
          const FileIcon = getFileIcon(doc.key);
          return (
            <ListItemButton
              key={doc.key}
              onClick={() => void downloadDocument(doc)}
              sx={{ borderRadius: 1, px: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={getDocumentName(doc)}
                primaryTypographyProps={{
                  variant: 'body2',
                  style: { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Stack>
  );
};
